import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useParams } from 'react-router';

import { Breadcrumb } from '@shared/chrome/Breadcrumb';
import { formatDate, toMachineDate } from '@shared/format';
import { caseStateBadgeKind } from '@shared/ui/case-state';
import { MetaGrid, Panel } from '@shared/ui/Panel';
import { StateBadge } from '@shared/ui/StateBadge';
import { SurfaceError } from '@shared/ui/SurfaceError';

import { CaseSection } from '../_shared/components';
import { caseStateLabel, caseTypeLabel, sharedCopy } from '../_shared/copy';
import { RESOLVABLE_CASE_TYPE, type CaseDetail } from '../_shared/types';
import { CaseDecision } from './CaseDetail.Decision';
import { CaseDetailSkeleton } from './CaseDetail.Skeleton';
import { CaseStateControl } from './CaseDetail.State';
import { CaseThread } from './CaseDetail.Thread';
import { copy } from './copy';
import { errors } from './errors';
import { useCaseStates, useSupportPermissions } from '../_shared/queries';
import { caseKey, useCase } from './queries';

/**
 * `/cases/:caseId` — one case worked from the operator's side.
 *
 * **The state sits beside the reason, not in the fifth row of a fact list.** It
 * is the first thing an operator wants when they open a case, and it is not
 * repeated below — one record, one state attribute, one place it is shown.
 *
 * **This page holds what an operator does to a case**: reply in the thread, move
 * the state, and — on a content report — decide what happens to the reported
 * title. They sit in matching panels for that reason: a bare form under a run of
 * messages reads as a sixth message that happens to contain a field, and a bare
 * select under that reads as a stray control.
 *
 * **The decision arrived here; it did not grow here.** It lived on
 * `/reports/:caseId`, a second surface over this same record — the report queue
 * was `GET /admin/cases?caseType=CONTENT_REPORT` and the report detail was this
 * page's own read. The endpoint the three buttons commit to,
 * `POST /admin/cases/{id}:resolve`, was always a case route. Two surfaces over
 * one record, dividing by verb, meant an operator reading a report here was told
 * in a footnote that the actions were somewhere else; now the record and every
 * act on it are in one place.
 *
 * **It is drawn on a content report and on nothing else.** The other four types
 * have no subject to unpublish or delete, and the owning service refuses them
 * with `422 SUPPORT_RESOLUTION_NOT_APPLICABLE` — one rule, two enforcement
 * points.
 */
export function CaseDetailPage() {
  const { caseId = '' } = useParams<{ caseId: string }>();
  const query = useCase(caseId);
  const caseStates = useCaseStates();
  const permissions = useSupportPermissions();
  const queryClient = useQueryClient();

  /**
   * One polite region serves both acts: each is the result of something the
   * operator intended, not an error interrupting them.
   */
  const [announcement, setAnnouncement] = useState('');

  const caseDetail = query.data;
  /** The block rider is `admin`-only on the owning service; the box follows it. */
  const canBlockOwner = permissions.data?.role.name === 'admin';

  return (
    <>
      <Breadcrumb
        items={[
          { label: sharedCopy.crumbs.console, to: '/' },
          { label: sharedCopy.crumbs.cases, ...(caseDetail ? { to: '/cases' } : {}) },
          ...(caseDetail ? [{ label: caseDetail.reason ?? caseDetail.caseReference }] : []),
        ]}
      />

      {query.isError ? (
        <div className="mt-6">
          <SurfaceError
            error={query.error}
            catalog={errors}
            onRetry={() => void queryClient.invalidateQueries({ queryKey: caseKey(caseId) })}
          />
        </div>
      ) : null}

      {query.isPending ? (
        <div className="mt-6">
          <CaseDetailSkeleton />
        </div>
      ) : null}

      {caseDetail ? (
        <>
          <div className="mb-6 mt-3 grid gap-stack-tight">
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="mb-0 font-display text-h1 font-bold">
                {caseDetail.reason ?? caseDetail.caseReference}
              </h1>
              <StateBadge kind={caseStateBadgeKind(caseDetail.caseState)}>
                {caseStateLabel(caseDetail.caseState)}
              </StateBadge>
            </div>
            <p className="mb-0 max-w-measure text-[1.0625rem] text-ink-muted">
              {copy.lede(
                caseTypeLabel(caseDetail.caseType),
                caseDetail.raiser?.displayName ?? sharedCopy.ownerUnknown,
              )}
            </p>
          </div>

          <p className="sr-only" aria-live="polite">
            {announcement}
          </p>

          <CaseFacts caseDetail={caseDetail} />

          {caseDetail.caseType === RESOLVABLE_CASE_TYPE ? (
            <CaseDecision
              caseId={caseDetail.id}
              caseDetail={caseDetail}
              canBlockOwner={canBlockOwner}
              onResolved={setAnnouncement}
            />
          ) : null}

          <CaseThread caseDetail={caseDetail} onReplied={setAnnouncement} />

          <CaseStateControl
            caseId={caseDetail.id}
            caseState={caseDetail.caseState}
            caseStates={caseStates.data ?? null}
            onApplied={setAnnouncement}
          />
        </>
      ) : null}
    </>
  );
}

/**
 * The case's own facts.
 *
 * The owner links to their account record — the catalog puts a case history
 * inside a user's detail, so the route between the two surfaces runs both ways
 * rather than one. **The name and nothing beside it**: on this platform a
 * subscriber who has not set a display name is served one that *is* their handle
 * — `backend-auth` mints `u<suffix>` for both — so the row read «u78zy01gtcg
 * @u78zy01gtcg», the same string twice. The account record this links to is where
 * the handle belongs.
 *
 * The subject row is drawn only on the two types that carry one; a case raised
 * against nothing has no subject, and an empty row would invent a field. Its
 * **owner** is drawn beside it, because the decision's block rider aims at that
 * account and a checkbox that blocks an unnamed party is not a decision an
 * operator can take.
 */
function CaseFacts({ caseDetail }: { caseDetail: CaseDetail }) {
  return (
    <CaseSection id="case-facts" heading={copy.facts.heading}>
      <Panel>
        <div className="p-4 md:p-5">
          <MetaGrid>
            <dt>{copy.facts.type}</dt>
            <dd>{caseTypeLabel(caseDetail.caseType)}</dd>

            <dt>{copy.facts.reason}</dt>
            <dd>{caseDetail.reason ?? caseDetail.caseReference}</dd>

            <dt>{copy.facts.reference}</dt>
            {/*
              The identifier is what either party quotes over the phone. ASCII, in
              the mono face, isolated — and the class sits on the inner element,
              because on the `<dd>` its `direction: ltr` flips the whole grid
              track and the value lands against the far edge.
            */}
            <dd>
              <bdi className="mono">{caseDetail.caseReference}</bdi>
            </dd>

            <dt>{copy.facts.openedAt}</dt>
            <dd>
              <time dateTime={toMachineDate(caseDetail.createdAt)}>
                <bdi>{formatDate(caseDetail.createdAt)}</bdi>
              </time>
            </dd>

            <dt>{copy.facts.owner}</dt>
            <dd>
              {caseDetail.raiser?.id ? (
                <Link to={`/users/${caseDetail.raiser.id}`} className="text-accent-text">
                  {caseDetail.raiser.displayName ?? sharedCopy.ownerUnknown}
                </Link>
              ) : (
                sharedCopy.ownerUnknown
              )}
            </dd>

            {caseDetail.subjectLabel ? (
              <>
                <dt>{copy.facts.subject}</dt>
                <dd>
                  {caseDetail.subjectId && caseDetail.subjectType === 'CONTENT_ITEM' ? (
                    <Link to={`/content/${caseDetail.subjectId}/edit`} className="text-accent-text">
                      {caseDetail.subjectLabel}
                    </Link>
                  ) : (
                    caseDetail.subjectLabel
                  )}
                </dd>
              </>
            ) : null}

            {caseDetail.subjectOwnerUserId ? (
              <>
                <dt>{copy.facts.subjectOwner}</dt>
                <dd>
                  <Link to={`/users/${caseDetail.subjectOwnerUserId}`} className="text-accent-text">
                    {caseDetail.subjectOwnerDisplayName ?? copy.facts.openSubjectOwner}
                  </Link>
                </dd>
              </>
            ) : null}
          </MetaGrid>
        </div>
      </Panel>
    </CaseSection>
  );
}
